import React, { useState } from 'react';
import { MiniLineChart } from '../finance/components/Charts';

/**
 * A revenue answer, as figures and a chart rather than a paragraph.
 *
 * USES THE APP'S OWN CHART. The first version hand-rolled SVG bars, which
 * meant a second chart style living beside the one Finance already ships —
 * different marks, different tooltip, different everything, in a product
 * where the two sit two taps apart. This imports Finance's MiniLineChart
 * instead: the same recharts line, the same dark tooltip, the same #007EA0
 * the Overview charts use. Imported, never edited.
 *
 * The form is right as well as consistent: revenue over days is
 * trend-over-time with one series, which is a line.
 *
 * A table view stays one tap away, so the numbers are never locked inside a
 * picture.
 */

const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
})}`;
const count = (n) => Number(n || 0).toLocaleString('en-GB');
const fmt = (t) => (t.format === 'money' ? money(t.value) : count(t.value));

// "2026-08-14" → "14 Aug". Parsed by parts rather than through Date, so a
// timezone can never shift a point onto the wrong day.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function shortDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!m) return String(iso || '');
    return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || ''}`;
}

export default function MetricsCard({ card }) {
    const [showTable, setShowTable] = useState(false);
    const series = (card.series || []).filter(p => p && p.date);

    // MiniLineChart wants { value, label }.
    const chartData = series.map(p => ({ value: p.value, label: shortDate(p.date) }));

    return (
        <div className="agt-card agt-metrics">
            <h4 className="agt-card-title">{card.title || 'Revenue'}</h4>
            {card.subtitle && <p className="agt-card-body">{card.subtitle}</p>}

            <div className="agt-tiles">
                {(card.tiles || []).map(t => (
                    <div className="agt-tile" key={t.label}>
                        <span className="agt-tile-k">{t.label}</span>
                        <span className="agt-tile-v">{fmt(t)}</span>
                    </div>
                ))}
            </div>

            {chartData.length > 1 && (
                <MiniLineChart
                    data={chartData}
                    color="#007EA0"
                    height={120}
                    valuePrefix="£"
                />
            )}

            {chartData.length > 1 && (
                <>
                    <button
                        className="agt-linkbtn"
                        onClick={() => setShowTable(v => !v)}
                        aria-expanded={showTable}
                    >
                        {showTable ? 'Hide the numbers' : 'Show the numbers'}
                    </button>
                    {showTable && (
                        <div className="agt-tablewrap">
                            <table className="agt-table">
                                <thead>
                                    <tr><th>Day</th><th>Revenue</th></tr>
                                </thead>
                                <tbody>
                                    {series.map(p => (
                                        <tr key={p.date}>
                                            <td>{shortDate(p.date)}</td>
                                            <td>{money(p.value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
